// src/components/expedientes/BuscarExpedienteModal.jsx
import React, { useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/img/logo.png";
import axios from "axios";
import { PATH } from "../../redux/utils";
import { SearchCombobox } from "../../components/ui";

const API_BASE = PATH;

const apiServiceGet = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});
const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
// ===============================
// 1. Comandos estáticos (front)
// ===============================
/**
 * Estos son "destinos" fijos del sistema.
 * Puedes agregar / quitar rutas sin tocar el backend.
 */
const STATIC_COMMANDS = [
  {
    key: "laboral-centro-conciliacion",
    type: "route",
    label: "Centro de conciliación (Laboral)",
    description: "Ir al panel de Centro de conciliación",
    route: "/materias/laboral/centro-conciliacion",
    keywords: ["centro", "conciliacion", "conciliación", "laboral", "prejudicial"],
  },
  {
    key: "laboral-tribunal",
    type: "route",
    label: "Tribunal laboral",
    description: "Ir al panel de Tribunal laboral",
    route: "/materias/laboral/tribunal",
    keywords: ["tribunal", "juicio", "tri", "laboral"],
  },
  {
    key: "laboral-junta-conciliacion",
    type: "route",
    label: "Junta de conciliación (Laboral)",
    description: "Ir al panel de Junta de conciliación",
    route: "/materias/laboral/junta-conciliacion",
    keywords: ["junta", "conciliacion", "conciliación", "laboral"],
  },
  {
    key: "laboral-documentacion",
    type: "route",
    label: "Documentación laboral",
    description: "Ir al módulo de documentación",
    route: "/materias/laboral/documentacion",
    keywords: ["documentacion", "documentación", "docs", "archivos", "laboral"],
  },
  {
    key: "laboral-desvinculaciones",
    type: "route",
    label: "Desvinculaciones (Laboral)",
    description: "Ir al panel de Desvinculaciones",
    route: "/materias/laboral/desvinculaciones",
    keywords: ["desvinculacion", "desvinculación", "desvinculaciones", "despido", "baja", "laboral"],
  },
];

function optionLabel(title, description) {
  return (
    <div>
      <div>{title}</div>
      <div style={{ fontSize: 12, color: "#6b7280" }}>{description}</div>
    </div>
  );
}

function buildStaticOptions(query) {
  const q = (query || "").toLowerCase().trim();
  const matches = !q
    ? STATIC_COMMANDS
    : STATIC_COMMANDS.filter((cmd) => {
        const hayEnLabel = cmd.label.toLowerCase().includes(q);
        const hayEnKeywords =
          Array.isArray(cmd.keywords) && cmd.keywords.some((k) => k.toLowerCase().includes(q));
        return hayEnLabel || hayEnKeywords;
      });

  return matches.map((cmd) => ({
    value: cmd.key,
    label: optionLabel(cmd.label, cmd.description),
    type: cmd.type,
    route: cmd.route,
  }));
}

function buildExpedienteOptions(items) {
  if (!Array.isArray(items)) return [];

  return items.map((item) => {
    const expedienteFormat = item.expediente_format || item.expediente || "";
    let title = expedienteFormat;

    const trabajador = item.trabajador || item.nombre_trabajador || "";
    const empresa = item.empresa || item.nombre_empresa || "";
    if (item.type === "expediente") {
      if (item.subtype === "desvinculacion" || item.subtype === "junta") {
        title = trabajador + " VS " + empresa;
      }
    }

    const secondaryParts = [];
    if (trabajador) secondaryParts.push(trabajador);
    if (empresa) secondaryParts.push(empresa);
    const secondary = secondaryParts.join(" • ");

    const materia = item.materia || "laboral";
    const procedimiento = item.procedimiento || "";
    const type = item.type || "expediente";
    const key = item.key;

    let seccionLabel = "Sección no especificada";
    if (materia === "laboral" && procedimiento === "centro-conciliacion") {
      seccionLabel = "Centro de conciliación (Laboral)";
    } else if (materia === "laboral" && procedimiento === "tribunal") {
      seccionLabel = "Tribunal laboral";
    } else if (materia === "laboral" && procedimiento === "documentacion") {
      seccionLabel = "Documentación laboral";
    } else if (materia === "laboral" && procedimiento === "desvinculaciones") {
      seccionLabel = "Desvinculaciones";
    } else if (materia === "laboral" && procedimiento === "tribunal") {
      seccionLabel = "Tribunal laboral";
    } else if (materia === "laboral" && procedimiento === "junta-conciliacion") {
      seccionLabel = "Junta de conciliación";
    }

    return {
      value: expedienteFormat,
      label: (
        <div>
          <div>
            <strong>{title}</strong>
          </div>
          <div style={{ fontSize: 12, color: "#6b7280" }}>{seccionLabel}</div>
          {secondary && <div style={{ fontSize: 12, color: "#9ca3af" }}>{secondary}</div>}
        </div>
      ),
      type,
      key,
      expediente_format: expedienteFormat,
      materia,
      procedimiento,
    };
  });
}

function BuscarExpedienteModal({ open, onClose }) {
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Reset cada vez que se abre / cierra
  useEffect(() => {
    if (!open) {
      setSearchValue("");
      setOptions([]);
    } else {
      // Al abrir puedes mostrar comandos estáticos por defecto
      setOptions(buildStaticOptions(""));
    }
  }, [open]);

  const fetchExpedientes = async (q) => {
    const query = (q || "").trim();
    if (!query) return [];

    try {
      setLoading(true);

      const [materiasRes, desvinRes] = await Promise.allSettled([
        apiServiceGet.get("materias/search", {
          params: { q: query, limit: 10 },
          headers: authHeader(),
        }),
        apiServiceGet.get("desvinculaciones", {
          params: { q: query, limit: 10 },
          headers: authHeader(),
        }),
      ]);

      const materiasData =
        materiasRes.status === "fulfilled" && Array.isArray(materiasRes.value.data)
          ? materiasRes.value.data
          : [];

      const desvinRaw =
        desvinRes.status === "fulfilled" && Array.isArray(desvinRes.value.data)
          ? desvinRes.value.data
          : [];

      const desvinData = desvinRaw.map((d) => ({
        ...d,
        type: "expediente",
        subtype: "desvinculacion",
        materia: "laboral",
        procedimiento: "desvinculaciones",
        key: d.id_desvinculacion ?? d.id ?? d.key,
        trabajador: d.nombre_solicitante || d.parte_actora || d.trabajador || d.nombre_trabajador || "",
        empresa: d.empresa_nombre || d.nombre_empresa || d.razon_social || d.empresa || "",
      }));

      // Evitar duplicados: si materias/search ya trajo la desvinculación, no la repetimos
      const materiasKeys = new Set(materiasData.map((m) => String(m.key ?? m.id ?? "")));
      const desvinUnicos = desvinData.filter((d) => !materiasKeys.has(String(d.key ?? "")));

      return [...materiasData, ...desvinUnicos];
    } catch (err) {
      console.error("Error al buscar:", err?.response?.status || err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    const trimmed = (value || "").trim();

    // Siempre construimos primero las opciones estáticas
    const staticOpts = buildStaticOptions(trimmed);

    // Si no hay nada escrito, solo mostramos comandos estáticos
    if (!trimmed) {
      setOptions(staticOpts);
      return;
    }

    // Debounce para no pegar al backend en cada tecla
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      const expedientes = await fetchExpedientes(trimmed);
      const expedienteOptions = buildExpedienteOptions(expedientes);

      // Combinamos estáticos + expedientes
      setOptions([...staticOpts, ...expedienteOptions]);
    }, 350);
  };

  const handleSelect = (option) => {
    setSearchValue(option?.value || "");
    const optType = option?.type;

    // 1) Navegación a secciones fijas
    if (optType === "route") {
      if (option.route) {
        navigate(option.route);
      } else {
        console.warn("Opción de tipo route sin 'route' definida:", option);
      }
      handleCloseInternal();
      return;
    }

    // 2) Abrir expediente
    if (optType === "expediente") {
      const materia = option.materia || "laboral";
      const procedimiento = option.procedimiento || "centro-conciliacion";

      // Aquí defines cómo se construye la ruta a detalle
      navigate(`/materias/${materia}/${procedimiento}/${option.key}`);

      handleCloseInternal();
      return;
    }

    // Si por alguna razón llega algo raro:
    console.warn("Opción seleccionada sin tipo conocido:", option);
  };

  const handleCloseInternal = () => {
    setSearchValue("");
    setOptions([]);
    if (typeof onClose === "function") onClose();
  };

  return (
    <Modal open={open} onCancel={handleCloseInternal} centered width={720} footer={null}>
      <div className="p-4 p-md-5">
        <div className="text-center mb-4">
          <img
            src={logo}
            alt="Logo"
            className="rounded-circle border p-2 mb-3"
            style={{ width: 72, height: 72, objectFit: "contain" }}
          />
          <h3 className="mb-1">Buscar sección o expediente</h3>
          <p className="text-secondary mb-0">
            Introduce el número de expediente o términos relacionados con la sección que deseas abrir.
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-search text-secondary" />
          <SearchCombobox
            className="flex-grow-1"
            value={searchValue}
            onChange={(text) => setSearchValue(text)}
            onSearch={handleSearch}
            onSelect={handleSelect}
            options={options}
            loading={loading}
            size="large"
            placeholder="Ejemplo: MXLI-CI-2025-000123, centro, tribunal..."
            notFoundContent="No se encontraron resultados."
          />
        </div>
      </div>
    </Modal>
  );
}

export default BuscarExpedienteModal;
